// This is a subset of the `ProjectSchema` interface with the necessary fields we need for
// determining a projects plan. The reason this exists and we don't import `ProjectSchema` directly
// here is importing that here would create a ciruclar dependency that will slow down our build
// processes.
interface ProjectSchemaSubset {
  flags: {
    enterprise: boolean;
  };
  is_active?: boolean;
  plan: PLAN_NAME;
  planOverride: PLAN_NAME | '';
  planStatus: string;
}

/** Combined view of both plan details and features. */
export type PlanConfig = PlanDetails & PlanFeatures;
/** Union of all plan config keys. */
export type PlanConfigKey = keyof PlanConfig;
/** Union of all plan feature keys. */
export type PlanFeatureKey = keyof PlanFeatures;

/**
 * General plan information like cost, beginning/end date, name, etc.
 * */
export interface PlanDetails {
  /** Total cost (in USD) for this plan. */
  cost: number;
  /** When this plan was started. */
  createdAt: Date;
  /** When this plan will end. */
  endDate?: Date;
  /** Whether plan is currently activated or not. */
  is_active: boolean;
  /** Whether this is a legacy/grandfathered plan that is no longer sold. */
  legacy?: boolean;
  /** What is the maximum amount of admins that this plan allows? */
  members: number;
  /** Plan name, e.g. `Startup`, `Business`. */
  name: string;
}

/**
 * Plan features, which are mostly `boolean` types that indicates whether a plan
 * supports this particular feature or not.
 */
export interface PlanFeatures {
  /**
   * Can this plan have users classified as a project admin?
   */
  admins: boolean;

  /** Does this plan have access to the AI Agent? */
  ai_agent: boolean;

  /** Does this plan have access to AI Writer (GitHub PR-driven docs updates)? */
  ai_writer: boolean;

  /** Does this plan have access to our API? */
  api_access: boolean;

  /** Does this plan have access to Ask AI? */
  askai: boolean;

  /** Does this plan have access to branch approval? */
  branch_approval: boolean;

  /** Does this plan have access to branches? */
  branches: boolean;

  /** Does this plan have access to changelogs? */
  changelogs: boolean;

  /** Can this plan create and manage custom components? */
  custom_components: boolean;

  /** Does this plan have access to set a custom domain? */
  custom_domain: boolean;

  /** Does this plan have access to custom pages? */
  custom_pages: boolean;

  /** Can this plan access discussion threads with users? */
  discussions: boolean;

  /** Can this plan access and view doc metrics? */
  doc_metrics: boolean;

  /** Does this plan have access to the docs audit and linting features? */
  docs_audit: boolean;

  /**
   * Can this plan configure custom 404 error pages?
   */
  errors: boolean;

  /** Can this plan set custom HTML in their header and footer? */
  extra_html: boolean;

  /** Can this plan use glossaries? */
  glossary: boolean;

  /** Can this plan create guides? */
  guides: boolean;

  /** Can this plan set up an integration with a health check service? */
  healthCheck: boolean;

  /** Can this plan set custom HTML in their landing page? */
  html: boolean;

  /** Can this plan have custom JS? */
  javascript: boolean;
  jwt: boolean;

  /** Can this plan create a global landing page (GLP)? */
  landing_page: boolean;

  /** Can this plan export metrics (CSV downloads)? */
  metricsExports: boolean;

  /** Can this plan password-protect their Hub? */
  password: boolean;

  /** Can this plan export pages as a PDF? */
  pdf: boolean;

  /** Can this plan access and create recipes? */
  recipes: boolean;

  /** Can this plan upload OpenAPI definitions? */
  reference: boolean;

  /** Can this plan create and update reusable content? */
  reusableContent: boolean;

  /** Can this plan have custom CSS? */
  stylesheet: boolean;

  /** Can this plan customize site navigation? */
  usability: boolean;

  /** Can this plan interact with backend services that use Clearbit for user introspection? */
  user_analytics: boolean;

  /** Does this plan have access to user variables? */
  variables: boolean;

  /** The number of versions that this plan can hold. */
  versions: number;

  /** Can this plan hide the ReadMe branding in the hub footer? */
  whitelabel: boolean;
}

/**
 * IF YOU MAKE CHANGES TO THIS CONFIG MAKE SURE TO UPDATE BOTH THE LEGACY CONFIGS TOO.
 *
 * @see @readme/legacy-angular-tooling/shared.js
 * @see @readme/backend/shared.js
 */
const basePlans = {
  free: {
    // NOTE: If we add this tier back in, make sure we only
    // allow trial accounts to have teammates (see getAllRegularProjects)
    name: 'Trial',
    members: 0,
    is_active: true,
    cost: 0,
    createdAt: new Date('January 1, 2010'),
    endDate: undefined,

    ai_agent: false,
    ai_writer: false,
    askai: false,
    docs_audit: false,

    api_access: true,
    branch_approval: false,
    branches: false,
    custom_domain: false,
    custom_pages: false,
    custom_components: true,
    errors: false,
    glossary: false,
    landing_page: false,
    password: false,
    pdf: false,
    versions: 3,
    whitelabel: false,

    doc_metrics: true,
    extra_html: false,
    html: false,
    javascript: false,
    jwt: true,
    stylesheet: false,
    usability: false,
    user_analytics: false,
    variables: false,

    changelogs: false,
    discussions: false,
    guides: false,
    recipes: false,
    reference: true,

    admins: false,
    healthCheck: false,

    metricsExports: false,
    reusableContent: false,
  },
  startup: {
    name: 'Startup',
    members: 10,
    is_active: true,
    cost: 59,
    createdAt: new Date('January 1, 2010'),
    endDate: new Date('August 28, 2018'),
    legacy: true,

    ai_agent: true,
    ai_writer: false,
    askai: false,
    docs_audit: true,

    api_access: true,
    branch_approval: false,
    branches: false,
    custom_domain: true,
    custom_pages: true,
    custom_components: true,
    errors: false,
    glossary: true,
    landing_page: true,
    password: true,
    pdf: false,
    versions: 10000,
    whitelabel: false,

    doc_metrics: true,
    extra_html: true,
    html: true,
    javascript: false,
    jwt: true,
    stylesheet: true,
    usability: true,
    user_analytics: false,
    variables: true,

    changelogs: true,
    discussions: true,
    guides: true,
    recipes: true,
    reference: true,

    admins: true,
    healthCheck: false,

    metricsExports: false,
    reusableContent: false,
  },
  business: {
    name: 'Business',
    is_active: true,
    members: 1000,
    cost: 199,
    createdAt: new Date('January 1, 2010'),
    endDate: new Date('August 28, 2018'),
    legacy: true,

    ai_agent: true,
    ai_writer: true,
    askai: true,
    docs_audit: true,

    api_access: true,
    branch_approval: false,
    branches: true,
    custom_domain: true,
    custom_pages: true,
    custom_components: true,
    errors: true,
    glossary: true,
    landing_page: true,
    password: true,
    pdf: true,
    versions: 10000,
    whitelabel: true,

    doc_metrics: true,
    extra_html: true,
    html: true,
    javascript: true,
    jwt: true,
    stylesheet: true,
    usability: true,
    user_analytics: true,
    variables: true,

    changelogs: true,
    discussions: true,
    guides: true,
    recipes: true,
    reference: true,

    admins: true,
    healthCheck: true,

    metricsExports: true,
    reusableContent: true,
  },
} as const satisfies Record<PLAN_NAME.BUSINESS | PLAN_NAME.FREE | PLAN_NAME.STARTUP, PlanConfig>;

/**
 * IF YOU MAKE CHANGES TO THIS CONFIG MAKE SURE TO UPDATE BOTH THE LEGACY CONFIGS TOO.
 *
 * @see `@readme/legacy-angular-tooling/shared.js`
 * @see `@readme/backend/shared.js`
 */
const permissions = {
  ...basePlans,

  freelaunch: {
    ...basePlans.free,
    admins: true,
    ai_writer: false,
    askai: false,
    cost: 0,
    createdAt: new Date('May 4, 2020'),
    custom_domain: true,
    custom_components: false,
    endDate: undefined,
    guides: true,
    members: 5,
    name: 'Free',
    reusableContent: false,
    stylesheet: false,
    versions: 3,
  },

  freelaunch2026: {
    ...basePlans.free,
    admins: true,
    ai_writer: false,
    askai: false,
    cost: 0,
    createdAt: new Date('January 14, 2026'),
    custom_domain: true,
    custom_components: false,
    endDate: undefined,
    guides: true,
    members: 1,
    name: 'Starter',
    reusableContent: false,
    stylesheet: false,
    versions: 1,
  },

  startup2018: {
    ...basePlans.startup,
    cost: 99,
    createdAt: new Date('August 28, 2018'),
    endDate: undefined,
  },

  'startup-annual-2024': {
    ...basePlans.startup,
    cost: 948,
    createdAt: new Date('March 27, 2024'),
    endDate: undefined,
    extra_html: false,
    stylesheet: false,
  },

  business2018: {
    ...basePlans.business,
    cost: 399,
    createdAt: new Date('August 28, 2018'),
    endDate: undefined,
    javascript: false,
    metricsExports: true,
  },

  'business-annual-2024': {
    ...basePlans.business,
    cost: 4188,
    createdAt: new Date('March 27, 2024'),
    endDate: undefined,
    javascript: false,
    metricsExports: true,
  },

  pro2026: {
    name: 'Pro',
    is_active: true,
    members: 1000,
    cost: 300,
    createdAt: new Date('January 14, 2026'),
    endDate: undefined,
    legacy: false,

    ai_agent: true,
    ai_writer: true,
    askai: true,
    docs_audit: true,

    api_access: true,
    branch_approval: false,
    branches: true,
    custom_domain: true,
    custom_pages: true,
    custom_components: true,
    errors: true,
    glossary: true,
    landing_page: true,
    password: true,
    pdf: true,
    versions: 10000,
    whitelabel: false,

    doc_metrics: true,
    extra_html: true,
    html: true,
    javascript: false,
    jwt: true,
    stylesheet: true,
    usability: true,
    user_analytics: true,
    variables: true,

    changelogs: true,
    discussions: true,
    guides: true,
    recipes: true,
    reference: true,

    admins: true,
    healthCheck: true,

    metricsExports: true,
    reusableContent: true,
    // TODO: Limit users/admins to 5
    // members: 5,
  },

  enterprise: {
    ...basePlans.business,
    ai_writer: true,
    cost: 0,
    createdAt: new Date('January 1, 2010'),
    branch_approval: true,
    endDate: undefined,
    javascript: true,
    legacy: false,
    members: 10000,
    metricsExports: true,
    name: 'Enterprise',
  },

  opensource: {
    ...basePlans.startup,
    cost: 0,
    createdAt: new Date('January 1, 2010'),
    custom_domain: false,
    endDate: undefined,
    extra_html: false,
    is_active: true,
    name: 'Open Source',
    password: false,
  },
} as const satisfies Record<
  | PLAN_NAME.BUSINESS
  | PLAN_NAME.BUSINESS_2018
  | PLAN_NAME.BUSINESS_ANNUAL_2024
  | PLAN_NAME.ENTERPRISE
  | PLAN_NAME.FREE
  | PLAN_NAME.FREELAUNCH
  | PLAN_NAME.FREELAUNCH2026
  | PLAN_NAME.OPENSOURCE
  | PLAN_NAME.PRO2026
  | PLAN_NAME.STARTUP
  | PLAN_NAME.STARTUP_2018
  | PLAN_NAME.STARTUP_ANNUAL_2024,
  PlanConfig
>;

// Define pro2026-annual after permissions so it can reference pro2026
const extendedPermissions = {
  ...permissions,
  'pro2026-annual': {
    ...permissions.pro2026,
    cost: 3000, // $250/month * 12 months
  },
} as const satisfies Record<PLAN_NAME, PlanConfig>;

export enum StripePlanInterval {
  DAY = 'day',
  MONTH = 'month',
  WEEK = 'week',
  YEAR = 'year',
}

export enum PLAN_NAME {
  BUSINESS = 'business',
  BUSINESS_2018 = 'business2018',
  BUSINESS_ANNUAL_2024 = 'business-annual-2024',
  ENTERPRISE = 'enterprise',
  FREE = 'free', // trial
  FREELAUNCH = 'freelaunch', // free post-trial period (legacy)
  FREELAUNCH2026 = 'freelaunch2026', // free post-trial period
  OPENSOURCE = 'opensource',
  PRO2026 = 'pro2026',
  PRO2026_ANNUAL = 'pro2026-annual',
  STARTUP = 'startup',
  STARTUP_2018 = 'startup2018',
  STARTUP_ANNUAL_2024 = 'startup-annual-2024',
}

export enum PlanCadence {
  MONTHLY = 'monthly',
  YEARLY = 'yearly',
}

export const PlanCadenceArray: PlanCadence[] = Object.values(PlanCadence);

export enum PlanType {
  FREE = 0 /* freelaunch */,
  TRIAL = 1 /* free */,
  STARTUP = 2 /* startup (legacy) or startup2018 or startup-annual-2024 or opensource */,
  BUSINESS = 3 /* business (legacy) or business2018 or business-annual-2024 */,
  ENTERPRISE = 4 /* enterprise */,
  DEV_DASH = 5 /* dev dash metrics metered billing */,
  PRO = 6 /* pro2026 or pro2026-annual */,
}

export const planNameToType: Record<PLAN_NAME, PlanType> = {
  [PLAN_NAME.FREELAUNCH]: PlanType.FREE,
  [PLAN_NAME.FREELAUNCH2026]: PlanType.FREE,
  [PLAN_NAME.FREE]: PlanType.TRIAL,
  [PLAN_NAME.OPENSOURCE]: PlanType.STARTUP,
  [PLAN_NAME.STARTUP]: PlanType.STARTUP,
  [PLAN_NAME.STARTUP_2018]: PlanType.STARTUP,
  [PLAN_NAME.BUSINESS]: PlanType.BUSINESS,
  [PLAN_NAME.BUSINESS_2018]: PlanType.BUSINESS,
  [PLAN_NAME.ENTERPRISE]: PlanType.ENTERPRISE,
  [PLAN_NAME.STARTUP_ANNUAL_2024]: PlanType.STARTUP,
  [PLAN_NAME.BUSINESS_ANNUAL_2024]: PlanType.BUSINESS,
  [PLAN_NAME.PRO2026]: PlanType.PRO,
  [PLAN_NAME.PRO2026_ANNUAL]: PlanType.PRO,
};

/** Check if a plan name maps to the free tier (freelaunch, freelaunch2026, etc.) */
export function isFreePlan(plan: PLAN_NAME | string): boolean {
  return planNameToType[plan as PLAN_NAME] === PlanType.FREE;
}

/** Check if a plan name maps to the trial tier */
export function isTrialPlan(plan: PLAN_NAME | string): boolean {
  return planNameToType[plan as PLAN_NAME] === PlanType.TRIAL;
}

/** Check if a plan name maps to either free or trial tier */
export function isFreeOrTrialPlan(plan: PLAN_NAME | string): boolean {
  return isFreePlan(plan) || isTrialPlan(plan);
}

export const planNameToCadence: Record<PLAN_NAME, PlanCadence> & {
  '': PlanCadence.MONTHLY;
} = {
  '': PlanCadence.MONTHLY, // Default to monthly
  [PLAN_NAME.FREELAUNCH]: PlanCadence.MONTHLY,
  [PLAN_NAME.FREELAUNCH2026]: PlanCadence.MONTHLY,
  [PLAN_NAME.FREE]: PlanCadence.MONTHLY,
  [PLAN_NAME.OPENSOURCE]: PlanCadence.MONTHLY,
  [PLAN_NAME.STARTUP]: PlanCadence.MONTHLY,
  [PLAN_NAME.STARTUP_2018]: PlanCadence.MONTHLY,
  [PLAN_NAME.BUSINESS]: PlanCadence.MONTHLY,
  [PLAN_NAME.BUSINESS_2018]: PlanCadence.MONTHLY,
  [PLAN_NAME.ENTERPRISE]: PlanCadence.MONTHLY,
  [PLAN_NAME.STARTUP_ANNUAL_2024]: PlanCadence.YEARLY,
  [PLAN_NAME.BUSINESS_ANNUAL_2024]: PlanCadence.YEARLY,
  [PLAN_NAME.PRO2026]: PlanCadence.MONTHLY,
  [PLAN_NAME.PRO2026_ANNUAL]: PlanCadence.YEARLY,
};

export const PlanNameArray: PLAN_NAME[] = Object.values(PLAN_NAME);

export enum ADD_ONS {
  AIBOOSTER_ANNUAL_2025 = 'aibooster-annual-2025',
  DEV_DASH_METERED_2024 = 'dev-dash-metered-2024',
  METRICS = 'metrics',
  OWLBOT = 'owlbot',
}

export const OWLBOT_LOOKUP_KEYS: readonly [ADD_ONS.OWLBOT, ADD_ONS.AIBOOSTER_ANNUAL_2025] = [
  ADD_ONS.OWLBOT,
  ADD_ONS.AIBOOSTER_ANNUAL_2025,
] as const;

// oxlint-disable perfectionist/sort-enums
export enum PLANS_AND_ADD_ONS {
  // project plans
  BUSINESS = 'business',
  BUSINESS_2018 = 'business2018',
  BUSINESS_ANNUAL_2024 = 'business-annual-2024',
  DEV_DASH_METERED_2024 = 'dev-dash-metered-2024',
  ENTERPRISE = 'enterprise',
  FREE = 'free',
  // trial
  FREELAUNCH = 'freelaunch',
  FREELAUNCH2026 = 'freelaunch2026',
  PRO2026 = 'pro2026',
  PRO2026_ANNUAL = 'pro2026-annual',
  STARTUP = 'startup',
  STARTUP_2018 = 'startup2018',
  STARTUP_ANNUAL_2024 = 'startup-annual-2024',
  // add on plans
  AIBOOSTER_ANNUAL_2025 = 'aibooster-annual-2025',
  METRICS = 'metrics', // old metrics plan (pre-2024, not metered)
  // free post-trial period
  OPENSOURCE = 'opensource',
  OWLBOT = 'owlbot',
}
// oxlint-enable perfectionist/sort-enums

// oxlint-disable perfectionist/sort-enums
export enum STRIPE_PLANS {
  // project plans
  BUSINESS = 'business',
  BUSINESS_2018 = 'business2018',
  BUSINESS_ANNUAL_2024 = 'business-annual-2024',
  DEV_DASH_METERED_2024 = 'dev-dash-metered-2024',
  FREE = 'free',
  // trial
  FREELAUNCH = 'freelaunch',
  FREELAUNCH2026 = 'freelaunch2026',
  PRO2026 = 'pro2026',
  PRO2026_ANNUAL = 'pro2026-annual',
  STARTUP = 'startup',
  STARTUP_2018 = 'startup2018',
  STARTUP_ANNUAL_2024 = 'startup-annual-2024',
  // add on plans
  AIBOOSTER_ANNUAL_2025 = 'aibooster-annual-2025',
  METRICS = 'metrics', // old metrics plan (pre-2024, not metered)
  OWLBOT = 'owlbot',
}
// oxlint-enable perfectionist/sort-enums

/**
 * Retrieve the config for a specific plan feature.
 */
export function getPlanFeature<
  Plan extends `${PLAN_NAME}` | undefined = undefined,
  Key extends PlanConfigKey | undefined = undefined,
  Value = Plan extends `${PLAN_NAME}` ? (Key extends PlanConfigKey ? PlanConfig[Key] : PlanConfig) : undefined,
>(plan?: Plan, key?: Key): Value {
  if (!plan) return undefined as Value;
  const planConfig = extendedPermissions[plan as PLAN_NAME];
  if (!planConfig) return undefined as Value;
  if (!key) return planConfig as Value;
  return planConfig[key as PlanConfigKey] as Value;
}

/**
 * Return the plan in which you must upgrade to in order to access a specific feature.
 */
export function getFeaturePlan(feature: PlanFeatureKey) {
  // Create a set of all currently available plans, excluding any of our legacy plans.
  const availablePlans = [PLAN_NAME.FREELAUNCH2026, PLAN_NAME.PRO2026, PLAN_NAME.ENTERPRISE];
  return availablePlans.find(plan => extendedPermissions[plan]?.[feature]) as PLAN_NAME;
}

export function getProjectPlan(project: ProjectSchemaSubset): PLAN_NAME {
  if (project.flags?.enterprise) return PLAN_NAME.ENTERPRISE;
  if (project.planOverride) return project.planOverride;
  // for delinquent projects, keep them on business2018 to avoid their hubs going offline
  if (project.planStatus === 'unpaid') return PLAN_NAME.BUSINESS_2018;
  // for trial projects (inactive + free plan), give them pro2026 features
  if (!project.is_active && (!project.plan || project.plan === 'free')) return PLAN_NAME.PRO2026;
  return project.plan;
}

function getPermission(plan?: `${PLAN_NAME}`, permission?: PlanConfigKey) {
  if (plan && permission) {
    const planConfig = extendedPermissions[plan];
    return planConfig?.[permission];
  } else if (plan) {
    return extendedPermissions[plan];
  }

  return extendedPermissions;
}

export function getProjectPermission<
  Key extends PlanConfigKey | undefined = undefined,
  Value = Key extends PlanConfigKey ? PlanConfig[Key] : PlanConfig,
>(project: ProjectSchemaSubset, permission?: Key): Value {
  if (!permission) {
    return getPermission(getProjectPlan(project)) as Value;
  }
  return getPermission(getProjectPlan(project), permission) as Value;
}
